import { defineStore } from 'pinia';
import { BOQItem } from '@/types/api/sites.types';
import { SelectedItems } from '@/types/api/build-boq';

export const useBuildBoqStore = defineStore('build-boq', {
  state: () => ({
    addSectionSubSectionOrGroupModalVisible: false as boolean,
    contextSectionPath: null as number[] | null,
    contextSubSectionPath: null as number[] | null,
    selectedItems: [] as SelectedItems[],
    buildActionType: 'section' as 'section' | 'sub-section' | 'group',
    selectedItemsActionType: 'group' as 'group' | 'delete' | 'add-to-cart',
    showCreateFromTemplateDialog: false as boolean,
  }),

  getters: {
    /**
     * Checks if an item is currently selected.
     * Returns a function to be called with the item's ID.
     */
    isItemSelected: (state) => {
      return (itemId: string) => state.selectedItems.some((item) => item.id === itemId);
    },

    /**
     * Determines if the 'Group Selected Items' button should be disabled.
     * This is true if any selected item within the given context path is already a group or part of one.
     * Returns a function to be called with the context path.
     */
    isGroupingDisabled: (state) => {
      return (contextPath: number[]) => {
        // Filter for selected items that are within the current section/sub-section
        const relevantItems = state.selectedItems.filter((item) =>
          isPathDescendant(item.path, contextPath),
        );
        // If any of these relevant items is already a group, disable the button
        return relevantItems.some((item) => item.isGroupOrGroupItem);
      };
    },
    /**
     * Checks if a given section or sub-section has any selected items.
     * Returns a function to be called with the context path.
     */
    hasSelectedItemsInSection: (state) => {
      return (contextPath: number[]) => {
        return state.selectedItems.some((item) => isPathDescendant(item.path, contextPath));
      };
    },
    /**
     * Gets a filtered list of selected item positions within a specific path that are eligible for grouping.
     * It filters out any items that are already groups or part of a group.
     * Returns a function to be called with the context path.
     */
    getGroupableItemsByPath: (state) => {
      return (contextPath: number[]): number[] => {
        const groupableItems = state.selectedItems.filter(
          (item) =>
            // Must be a descendant of the provided path
            isPathDescendant(item.path, contextPath) &&
            // Must NOT be a group or an item within a group
            !item.isGroupOrGroupItem,
        );
        // Return only the 'position' property
        return groupableItems.map((item) => item.position);
      };
    },
  },

  actions: {
    toggleModal(open: boolean) {
      this.addSectionSubSectionOrGroupModalVisible = open;
    },

    setFormContext(context: { sectionPath?: number[] | null; subSectionPath?: number[] | null }) {
      this.contextSectionPath = context.sectionPath || null;
      this.contextSubSectionPath = context.subSectionPath || null;
    },

    clearFormContext() {
      this.contextSectionPath = null;
      this.contextSubSectionPath = null;
    },

    clearSelectedItems() {
      this.selectedItems = [];
    },

    /**
     * Toggles the selection status of a BOQ item.
     * Adds the item to selectedItems if not present, removes it otherwise.
     */
    toggleSelectedItem(item: BOQItem, isGroup: boolean) {
      const existingItemIndex = this.selectedItems.findIndex((i) => i.id === item.id);

      if (existingItemIndex === -1) {
        // Item is not selected, so add it
        this.selectedItems.push({
          id: item.id,
          name: item.name,
          position: item.position,
          isGroupOrGroupItem: isGroup,
          path: item.path,
          hasPendingVariation: item.hasPendingVariation,
          vendorItemUnavailable: item.vendorItemUnavailable,
        });
      } else {
        // Item is already selected, so remove it
        this.selectedItems.splice(existingItemIndex, 1);
      }
    },
    /**
     * Removes items from the selection based on an array of item IDs.
     */
    removeSelectedItemsById(itemIds: string[]) {
      const deletedIdsSet = new Set(itemIds);
      this.selectedItems = this.selectedItems.filter((item) => !deletedIdsSet.has(item.id));
    },
  },
});

const isPathDescendant = (childPath: number[], parentPath: number[]): boolean => {
  if (parentPath.length > childPath.length) {
    return false;
  }
  return parentPath.every((segment, index) => segment === childPath[index]);
};
